<script setup>
// Workspace → Mapas: lista dos mapas mentais/workflows da conta (canvas
// estilo Miro). Clique abre o canvas; renomear acontece dentro do mapa.
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { planningMapsAPI } from 'dashboard/api/workspace';

const { t } = useI18n();
const router = useRouter();

const isLoading = ref(true);
const maps = ref([]);

const fetchMaps = async () => {
  isLoading.value = true;
  try {
    const { data } = await planningMapsAPI.get();
    maps.value = data || [];
  } catch {
    maps.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openMap = id => {
  router.push({ name: 'planning_map_view', params: { mapId: id } });
};

const createMap = async () => {
  try {
    const { data } = await planningMapsAPI.create({});
    openMap(data.id);
  } catch {
    useAlert(t('WORKSPACE_MAPS.CREATE_ERROR'));
  }
};

const deleteMap = async map => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('WORKSPACE_MAPS.DELETE_CONFIRM', { name: map.name })))
    return;
  try {
    await planningMapsAPI.delete(map.id);
    maps.value = maps.value.filter(m => m.id !== map.id);
  } catch {
    useAlert(t('WORKSPACE_MAPS.DELETE_ERROR'));
  }
};

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

onMounted(fetchMaps);
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto bg-n-background">
    <div class="px-6 py-5 max-w-5xl w-full mx-auto space-y-5">
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('WORKSPACE_MAPS.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-10">
            {{ t('WORKSPACE_MAPS.SUBTITLE') }}
          </p>
        </div>
        <button
          class="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-n-brand text-white hover:bg-n-brand/90 transition-colors"
          @click="createMap"
        >
          <span class="i-lucide-plus w-4 h-4" />
          {{ t('WORKSPACE_MAPS.NEW') }}
        </button>
      </div>

      <div v-if="isLoading" class="flex justify-center py-12">
        <span
          class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
        />
      </div>

      <div
        v-else-if="!maps.length"
        class="flex flex-col items-center gap-3 py-16 text-n-slate-10"
      >
        <span class="i-lucide-git-fork w-10 h-10" />
        <p class="text-sm">{{ t('WORKSPACE_MAPS.EMPTY') }}</p>
      </div>

      <div v-else class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="map in maps"
          :key="map.id"
          class="group p-4 rounded-xl bg-n-solid-2 border border-n-weak hover:border-n-brand/50 transition-colors cursor-pointer"
          @click="openMap(map.id)"
        >
          <div class="flex items-start justify-between gap-2">
            <p class="text-sm font-medium text-n-slate-12 line-clamp-2">
              {{ map.name }}
            </p>
            <button
              class="opacity-0 group-hover:opacity-100 p-1 rounded-lg text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2 transition-all"
              :title="t('WORKSPACE_MAPS.DELETE')"
              @click.stop="deleteMap(map)"
            >
              <span class="i-lucide-trash-2 w-4 h-4" />
            </button>
          </div>
          <p class="mt-2 text-xs text-n-slate-10">
            {{ t('WORKSPACE_MAPS.UPDATED_AT', { date: formatDate(map.updated_at) }) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
